import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReportDownload from '../../components/ReportDownload';
import ProviderSelector from '../../components/traceability/ProviderSelector';
import ZoneSelector from '../../components/ZoneSelector';
import { zonesAPI } from '../../api/client';
import { captureLocation, evidenceStamp, validPoint } from '../../lib/fieldOperation';
import {
  captureBeforeNA,
  undoPendingNA,
  reconcileItems,
  serverItems,
  RESPONSE_FIELDS,
} from '../../lib/checklistReconcile';
import { isCriticalFinding } from '../../lib/answerFormat';
import {
  binaryLabels,
  displayStatus,
  errorMessage,
  evaluationProgress,
  evaluationPercentage,
  localMissing,
  missingFromError,
  publicationWarning,
  percentageLabel,
  shouldAutofillDate,
  scaleValues,
  visibilityMap,
} from '../../lib/evaluationIntegrity';
import { IntegrityFields, ScoreSummary, MissingList } from '../../components/EvaluationIntegrity';
import { getSectionStatsFromEval, sectionKey, sectionTitle } from '../../lib/evalSections';
import { useParams, useNavigate } from 'react-router-dom';
import { evaluationsAPI, projectsAPI, usersAPI } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import {
  CRITERIA,
  SECTIONS,
  SCORE_LABELS,
  getCriteriaBySection,
  BLOCKER_CRITERIA,
  getSemaforoBarColor,
} from '../../lib/criteria';
import {
  markDirty,
  snapshotDirty,
  clearSaved,
  clearSkipped,
  hasDirtyItems,
  buildItemsPayload,
  isScored,
} from '../../lib/checklistSave';
import { getEvidenceUrl } from '../../lib/fileUrls';
import SafeImage from '../../components/common/SafeImage';
import SemaforoBadge from '../../components/SemaforoBadge';
import { SectionRadarChart } from '../../components/ScoreChart';
import StatusBadge from '../../components/StatusBadge';
import { DictamenCard } from '../../lib/dictamen';
import {
  ArrowLeft,
  AlertTriangle,
  Save,
  Send,
  Upload,
  CheckCircle,
  Image,
  Mic,
  Video,
  Map,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { notifySubmitted } from '../../api/notifications';

const SCORE_COLORS = {
  0: 'border-red-400 bg-red-50 text-red-700 hover:bg-red-100',
  2: 'border-red-400 bg-red-50 text-red-700 hover:bg-red-100',
  4: 'border-yellow-400 bg-yellow-50 text-yellow-700 hover:bg-yellow-100',
  6: 'border-yellow-400 bg-yellow-50 text-yellow-700 hover:bg-yellow-100',
  8: 'border-green-400 bg-green-50 text-green-700 hover:bg-green-100',
  10: 'border-blue-500 bg-blue-100 text-blue-800 hover:bg-blue-200'
};

const SECTION_CONFIG = {
  'EVENTOS': { label: 'SECCIÓN 1 — EVALUACIÓN DE EVENTOS', color: '#1A56DB', range: '1-12' },
  'SECURITY': { label: 'SECCIÓN 2 — SECURITY', color: '#7C3AED', range: '13-26' },
  'SAFETY': { label: 'SECCIÓN 3 — SAFETY', color: '#059669', range: '27-35' },
};

// Question type rendering helpers (#198). Templates may declare any of
// these types per question; the eval UI used to hardcode score_scale.
// #Fase1-10 — Las claves de captura son independientes del título visible de cada sección.
function indexTemplateCriteria(sections) {
  const result = {};
  sections.forEach((section) => {
    (section.criteria || []).forEach((criterion) => {
      const key = criterion.criterion_id ?? criterion.question_id;
      if (key != null) result[key] = criterion;
    });
  });
  return result;
}

function formatAddress(address) {
  if (!address || typeof address === 'string') return address;
  return [address.street, address.ext_number, address.neighborhood, address.city, address.state]
    .filter(Boolean)
    .join(', ');
}

// #Fase1-10 — Autollenado de hoy solo al montar una fecha elegible y nunca tocada (#198).
export function DateAnswerInput({ item, readOnly, onAnswer, autoFill }) {
  // #Fase1-10 — El padre recuerda intentos y vaciados incluso al cambiar de sección.
  useEffect(() => {
    if (autoFill && !readOnly && !item.not_applicable && !item.answer_date) {
      const today = new Date();
      const date = [
        today.getFullYear(),
        String(today.getMonth() + 1).padStart(2, '0'),
        String(today.getDate()).padStart(2, '0'),
      ].join('-');
      onAnswer({ answer_date: date });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <input
      type="date"
      disabled={readOnly}
      value={item?.answer_date || ''}
      onChange={(e) => onAnswer({ answer_date: e.target.value || null })}
      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary disabled:bg-slate-50"
    />
  );
}

function QuestionInput({ criterion, item, readOnly, onAnswer }) {
  const qtype = criterion.question_type || 'score_scale';
  const options = criterion.options || [];

  if (qtype === 'score_scale') {
    // #Fase0-B2 — los items nacen con score 0; ese 0 no es una respuesta.
    // Solo se pinta un valor si el reactivo se calificó (score_set).
    const scored = isScored(item);
    if (readOnly) {
      if (!scored) {
        return <span className="inline-flex items-center px-3 py-1.5 rounded-sm border border-slate-200 text-sm text-slate-400 italic">Sin calificar</span>;
      }
      return (
        <span className={`inline-flex items-center px-4 py-2 rounded-sm border-2 font-bold text-xl ${SCORE_COLORS[item.score] || SCORE_COLORS[0]}`}>
          {item.score}/{criterion.max_score ?? 10}
        </span>
      );
    }
    return (
      <div className="grid grid-cols-6 gap-1.5 sm:flex sm:flex-wrap sm:gap-2">
        {scaleValues(criterion).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onAnswer({ score: s, has_been_scored: true, score_set: true })}
            className={`h-11 sm:w-12 sm:h-12 rounded-sm border-2 font-bold text-base sm:text-lg transition-all active:scale-95 ${
              scored && item.score === s
                ? `${SCORE_COLORS[s] || 'border-soem-primary bg-blue-50 text-soem-primary'} ` +
                  'ring-2 ring-offset-1 ring-soem-primary font-black'
                : 'border-slate-300 text-slate-500 hover:border-slate-400 bg-white'
            }`}
            title={SCORE_LABELS?.[s] || `Puntaje ${s}`}
            data-testid={`score-btn-${criterion.id}-${s}`}
          >
            {s}
          </button>
        ))}
      </div>
    );
  }

  if (qtype === 'info')
    return <div className="bg-blue-50 p-3 rounded-sm text-sm">Informativo · Solo lectura</div>;
  if (binaryLabels[qtype]) {
    return (
      <div className="flex gap-2">
        {binaryLabels[qtype]
          .map((label, i) => ({ v: i === 0, label }))
          .map(opt => (
          <button
            key={String(opt.v)}
            type="button"
            disabled={readOnly}
            onClick={() => onAnswer({ answer_bool: opt.v })}
            className={`flex-1 px-4 py-2.5 rounded-sm border-2 font-semibold text-sm transition-all ${
              item?.answer_bool === opt.v
                ? 'border-soem-primary bg-soem-primary/10 text-soem-primary'
                : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400'
            } ${readOnly ? 'opacity-70 cursor-not-allowed' : 'active:scale-95'}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    );
  }

  if (qtype === 'dropdown') {
    return (
      <select
        disabled={readOnly}
        value={(item?.selected_options || [])[0] || ''}
        onChange={(e) => onAnswer({ selected_options: e.target.value ? [e.target.value] : [] })}
        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-soem-primary disabled:bg-slate-50"
      >
        <option value="">Seleccionar...</option>
        {options.map((opt, i) => (
          <option key={i} value={opt.label}>{opt.label}</option>
        ))}
      </select>
    );
  }

  if (qtype === 'single_choice') {
    return (
      <div className="space-y-2">
        {options.map((opt, i) => {
          // Use label as the canonical identifier; option.value is 0 by
          // default for non-scoring types (#198 — would collide across
          // options and break "checked" detection).
          const v = opt.label;
          const checked = (item?.selected_options || [])[0] === v;
          return (
            <label key={i} className={`flex items-center gap-2 p-2 border rounded-sm cursor-pointer ${checked ? 'border-soem-primary bg-soem-primary/5' : 'border-slate-200 hover:border-slate-300'} ${readOnly ? 'opacity-70 cursor-not-allowed' : ''}`}>
              <input
                type="radio"
                name={`q-${criterion.id}`}
                disabled={readOnly}
                checked={checked}
                onChange={() => onAnswer({ selected_options: [v] })}
              />
              <span className="text-sm text-slate-700">{opt.label}</span>
            </label>
          );
        })}
      </div>
    );
  }

  if (qtype === 'multiple_choice') {
    const selected = item?.selected_options || [];
    return (
      <div className="space-y-2">
        {options.map((opt, i) => {
          // Use label as the canonical identifier; option.value is 0 by
          // default for non-scoring types (#198 — would collide across
          // options and break "checked" detection).
          const v = opt.label;
          const checked = selected.includes(v);
          return (
            <label key={i} className={`flex items-center gap-2 p-2 border rounded-sm cursor-pointer ${checked ? 'border-soem-primary bg-soem-primary/5' : 'border-slate-200 hover:border-slate-300'} ${readOnly ? 'opacity-70 cursor-not-allowed' : ''}`}>
              <input
                type="checkbox"
                disabled={readOnly}
                checked={checked}
                onChange={() => {
                  const next = checked ? selected.filter(x => x !== v) : [...selected, v];
                  onAnswer({ selected_options: next });
                }}
              />
              <span className="text-sm text-slate-700">{opt.label}</span>
            </label>
          );
        })}
      </div>
    );
  }

  if (qtype === 'numeric') {
    // Negativos rechazados por defecto. min_value/max_value vienen del template.
    const minV = criterion.min_value ?? 0;
    const maxV = criterion.max_value;
    const handleNumericChange = (raw) => {
      if (raw === '') { onAnswer({ answer_numeric: null }); return; }
      let n = Number(raw);
      if (Number.isNaN(n)) return;
      // Clamp a min/max para no permitir guardar fuera de rango.
      if (n < minV) n = minV;
      if (maxV != null && n > maxV) n = maxV;
      onAnswer({ answer_numeric: n });
    };
    return (
      <div>
        <input
          type="number"
          disabled={readOnly}
          value={item?.answer_numeric ?? ''}
          min={minV}
          max={maxV ?? undefined}
          step="any"
          onChange={(e) => handleNumericChange(e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary disabled:bg-slate-50"
          placeholder={criterion.placeholder || 'Ingresa el número'}
        />
        {(criterion.min_value != null || criterion.max_value != null) && (
          <p className="text-xs text-slate-500 mt-1">
            Rango permitido: {criterion.min_value ?? '—'} a {criterion.max_value ?? '∞'}
          </p>
        )}
      </div>
    );
  }

  if (qtype === 'date') {
    return (
      <DateAnswerInput
        autoFill={criterion._autofillDate}
        item={item} readOnly={readOnly} onAnswer={onAnswer} />
    );
  }

  if (qtype === 'text_short') {
    return (
      <input
        type="text"
        disabled={readOnly}
        value={item?.answer_text || ''}
        onChange={(e) => onAnswer({ answer_text: e.target.value })}
        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary disabled:bg-slate-50"
        placeholder={criterion.placeholder || 'Ingresa tu respuesta'}
      />
    );
  }

  if (qtype === 'text_long') {
    return (
      <textarea
        rows={4}
        disabled={readOnly}
        value={item?.answer_text || ''}
        onChange={(e) => onAnswer({ answer_text: e.target.value })}
        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none focus:outline-none focus:ring-2 focus:ring-soem-primary disabled:bg-slate-50"
        placeholder={criterion.placeholder || 'Describe en detalle...'}
      />
    );
  }

  if (qtype === 'file_upload') {
    // The Evidence section below already handles file uploads; show a hint.
    return (
      <p className="text-xs text-slate-500 italic">
        Sube los archivos solicitados en la sección de Evidencia más abajo.
      </p>
    );
  }

  // Unknown / unsupported type — fall back to score scale.
  return null;
}

function CriterionCard({ criterion, item, readOnly, onScoreChange, onAnswerChange, onNotesChange, onEvidenceUpload,
  onEvidenceDelete, zones, onLocationUpdate, eventId,
  allowNA,
  uploading, index, total,
}) {
  const [localNotes, setLocalNotes] = useState(item?.notes || '');
  const fileRef = useRef(null);
  // #Fase1-10 — Las notas remotas se concilian sin reemplazar una edición todavía enfocada.
  const notesEditingRef = useRef(false);
  useEffect(() => {
    if (!notesEditingRef.current) setLocalNotes(item?.notes || '');
  }, [item?.notes]);
  // #Fase1-10 — El bloqueo local usa la respuesta y visibilidad actuales, no el GET inicial.
  const hasScore =
    !item?.not_applicable &&
    (criterion.question_type || 'score_scale') === 'score_scale' && isScored(item);
  const isBlocked = isCriticalFinding(criterion, item, true);

  // #RecintoSeguro — Para score_scale, deriva la opción elegida desde las
  // opciones de la plantilla (lo que se editó en el Constructor). Si la
  // plantilla no trae options, usa el catálogo legacy como respaldo.
  const scaleOptions = Array.isArray(criterion.options) ? criterion.options : [];
  const selectedOption = hasScore
    ? scaleOptions.find(o => Number(o.value) === Number(item.score))
    : null;
  const levelLabel = selectedOption?.label || SCORE_LABELS?.[item?.score];
  const levelDescription = selectedOption?.description || criterion.descriptions?.[item?.score];
  const recommendation = hasScore
    ? (selectedOption?.recommendation || criterion.recommendations?.[item.score] || null)
    : null;

  // Get criterion text - use title as main text, or fallback
  const criterionText = criterion.title || `Criterio #${criterion.id}`;
  const criterionFocus = criterion.focus || '';

  // #Fase1-2 — Un bloque informativo no ofrece captura ni evidencia.
  if (criterion.question_type === 'info')
    return (
      <div className="soem-card p-4 bg-blue-50">
        <IntegrityFields criterion={criterion} item={item} />
        <h4 className="font-bold text-soem-navy">{criterionText}</h4>
        <p className="text-sm">{criterionFocus}</p>
        <p className="text-sm text-slate-600">{criterion.recintoSeguro}</p>
      </div>
    );

  const handleNotesBlur = () => {
    notesEditingRef.current = false;
    if (localNotes !== (item?.notes || '')) onNotesChange(criterion.id, localNotes);
  };

  return (
    <div
      className={`border-2 rounded-sm transition-all ${isBlocked ? 'border-red-500 bg-red-50/50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
      data-testid={`criterion-card-${criterion.id}`}
    >
      {/* Header with criterion number and score indicator */}
      <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 sm:px-4 py-2 sm:py-3 border-b gap-2 ${isBlocked ? 'bg-red-100 border-red-300' : 'bg-slate-50 border-slate-100'}`}>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <span className={`text-xs font-bold px-2 py-1 rounded ${isBlocked ? 'bg-red-600 text-white' : 'bg-soem-navy text-white'}`}>
            {index + 1}/{total}
          </span>
          {criterion.blocker && (
            <span className="bg-red-600 text-white text-xs px-2 py-1 rounded font-bold flex items-center gap-1">
              <AlertTriangle size={10} /> <span className="hidden sm:inline">CRITERIO</span> BLOQUEANTE
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasScore && (
            <span className={`text-sm font-black px-2 py-0.5 rounded ${
              item.score === 10 ? 'bg-blue-100 text-blue-700' :
              item.score >= 8 ? 'bg-green-100 text-green-700' : 
              item.score >= 4 ? 'bg-yellow-100 text-yellow-700' : 
              'bg-red-100 text-red-700'
            }`}>
              {item.score}/{criterion.max_score ?? 10}
            </span>
          )}
          {hasScore && item.score >= 6 && !isBlocked && <CheckCircle size={16} className="text-green-600" />}
        </div>
      </div>

      {/* Blocker warning */}
      {isBlocked && (
        <div className="bg-red-600 text-white text-sm font-bold px-4 py-2 flex items-center gap-2">
          <AlertTriangle size={14} />
          🚨 EVALUACIÓN BLOQUEADA — Se publicará con bloqueo
        </div>
      )}

      {/* Criterion content */}
      <div className="p-3 sm:p-4">
        {/* Criterion text - FULL TEXT */}
        <div className="mb-3 sm:mb-4">
          <h4 className="font-bold text-soem-navy text-sm sm:text-base leading-tight mb-1">
            {criterionText}
          </h4>
          {criterionFocus && (
            <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
              {criterionFocus}
            </p>
          )}
          {/* Recinto Seguro - ALWAYS gray background */}
          {criterion.recintoSeguro && (
            <div className="mt-2 p-2 rounded-sm text-xs sm:text-sm border-l-4 bg-slate-100 border-slate-400 text-slate-700">
              <p className="font-semibold text-xs uppercase tracking-wide opacity-70 mb-0.5">
                Recinto Seguro:
              </p>
              <p className="leading-relaxed">{criterion.recintoSeguro}</p>
            </div>
          )}
        </div>

        <IntegrityFields
          criterion={criterion}
          item={item}
          allowNA={allowNA && !readOnly}
          recommendation
          onChange={!readOnly ? (partial) => onAnswerChange(criterion.id, partial) : undefined}
        />
        {criterion.requires_provider && !item?.not_applicable && (
          <ProviderSelector eventId={eventId} requirement={criterion.requires_provider}
            value={item?.provider_ids || []} disabled={readOnly}
            onChange={(provider_ids) => onAnswerChange(criterion.id, { provider_ids })} />
        )}
        {/* Answer input — switches by question_type (#198). */}
        <div className="mb-4">
          {(criterion.question_type || 'score_scale') === 'score_scale' && !readOnly && (
            <label htmlFor={`q-input-${criterion.id}`} className="soem-label block mb-2">Calificación:</label>
          )}
          {!item?.not_applicable && (
            <QuestionInput
            criterion={criterion}
            item={item}
            readOnly={readOnly}
            onAnswer={(partial) => {
              if (partial.score !== undefined) {
                onScoreChange(criterion.id, partial.score);
              } else {
                onAnswerChange && onAnswerChange(criterion.id, partial);
              }
            }}
          />
          )}
          {/* #RecintoSeguro — Etiqueta del valor según la calificación
              elegida (sale de las opciones de la plantilla). */}
          {(criterion.question_type || 'score_scale') === 'score_scale' && hasScore && levelLabel && (
            <p className="text-xs font-semibold text-soem-primary mt-1.5">
              {levelLabel}
            </p>
          )}
        </div>

        {/* #RecintoSeguro — Descripción del nivel seleccionado */}
        {hasScore && levelDescription && (
          <div className="p-3 rounded-sm text-sm mb-4 bg-slate-50 border border-slate-200">
            <p className="font-semibold text-xs mb-1 uppercase tracking-wide text-slate-500">Descripción del nivel {item.score}:</p>
            <p className="leading-relaxed text-slate-700">{levelDescription}</p>
          </div>
        )}

        {/* Recommendation */}
        {recommendation && (
          <div className={`p-3 rounded-sm text-sm mb-4 border-l-4 ${
            item.score === 10 ? 'bg-blue-50 border-blue-500 text-blue-800' :
            item.score >= 8 ? 'bg-green-50 border-green-500 text-green-800' :
            item.score >= 4 ? 'bg-yellow-50 border-yellow-500 text-yellow-800' :
            'bg-red-50 border-red-500 text-red-800'
          }`}>
            <p className="font-semibold text-xs mb-1 uppercase tracking-wide opacity-70">Recomendación:</p>
            <p className="leading-relaxed">{recommendation}</p>
          </div>
        )}

        {/* Notes textarea */}
        {!readOnly && (
          <div className="mb-4">
            <label htmlFor="f-field" className="soem-label block mb-1.5 flex items-center gap-1">
              <FileText size={12} /> Notas del criterio:
            </label>
            <textarea
id="f-field"               className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none focus:outline-none focus:ring-2 focus:ring-soem-primary focus:border-transparent"
              rows={3}
              placeholder="Escribe tus observaciones sobre este criterio..."
              value={localNotes}
              onFocus={() => {
                notesEditingRef.current = true;
              }}
              onChange={(e) => {
                setLocalNotes(e.target.value);
                onNotesChange(criterion.id, e.target.value);
              }}
              onBlur={handleNotesBlur}
              data-testid={`notes-${criterion.id}`}
            />
          </div>
        )}

        {/* Read-only notes */}
        {readOnly && item?.notes && (
          <div className="mb-4 p-3 bg-slate-50 rounded-sm border border-slate-200">
            <p className="soem-label text-xs mb-1">Notas del levantador:</p>
            <p className="text-slate-700 text-sm">{item.notes}</p>
          </div>
        )}

        <div className="mb-4 space-y-2">
          <ZoneSelector zones={zones} zone={item?.zone} disabled={readOnly}
            onChange={(zone) => onAnswerChange(criterion.id, { zone: zone.id ? zone : null })} />
          {(item?.location || item?.zone) && <p className="text-xs">
            Ubicación registrada: {evidenceStamp(item)}
            {item.location && validPoint(item.location) && <span> · {item.location.lat}, {item.location.lng}</span>}
          </p>}
          {!readOnly && <button type="button" className="soem-btn-secondary p-2 text-sm"
            onClick={() => onLocationUpdate(criterion.id)}>Actualizar ubicación</button>}
        </div>
        {/* Evidence */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="f-field-1" className="soem-label flex items-center gap-1">
              <Image size={12} /> Evidencia
            </label>
            {!readOnly && (
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading !== null}
                className="soem-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-50"
              >
                <Upload size={12} /> {uploading === criterion.id ? 'Subiendo...' : 'Agregar evidencia'}
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*,video/*,audio/*,application/pdf"
              disabled={readOnly || uploading !== null}
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) onEvidenceUpload(criterion.id, Array.from(e.target.files));
                e.target.value = '';
              }}
            />
          </div>
          {item?.evidence_urls?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {item.evidence_urls.map((url, i) => {
                const fullUrl = getEvidenceUrl(url);
                const metadata = item.evidence?.find((entry) => entry.file_url === url);
                const isImage = url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                return (
                  <div key={url}>
                    <a
                      href={fullUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-16 h-16 bg-slate-100 rounded-sm border border-slate-200 flex items-center justify-center hover:border-soem-primary transition-colors overflow-hidden"
                  >
                    {isImage ? (
                      <SafeImage 
                        src={url}
                        type="evidence"
                        alt={`Evidencia ${i + 1}`}
                        className="w-full h-full"
                        imgClassName="w-full h-full object-cover"
                        showFallback={true}
                        fallbackIcon={<Image size={20} className="text-slate-400" />}
                      />
                    ) : metadata?.media_type === 'document' || /\.pdf(?:[?#]|$)/i.test(url) ? (
                      <span className="text-xs font-bold">PDF</span>
                    ) : url.match(/\.(mp4|webm|mov)$/i) ? (
                      <Video size={20} className="text-slate-400" />
                    ) : url.match(/\.(mp3|wav|ogg)$/i) ? (
                      <Mic size={20} className="text-slate-400" />
                    ) : (
                      <Image size={20} className="text-slate-400" />
                    )}
                  </a>
                    {metadata && <p className="text-xs max-w-40">{evidenceStamp(metadata)}</p>}
                    {!readOnly && (
                      <button
                        type="button"
                        className="soem-btn-secondary text-xs p-1"
                        onClick={() => onEvidenceDelete(criterion.id, url)}
                      >
                        Borrar evidencia {i + 1}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {(!item?.evidence_urls || item.evidence_urls.length === 0) && readOnly && (
            <p className="text-slate-400 text-xs italic">Sin evidencia adjunta</p>
          )}
        </div>
      </div>
    </div>
  );
}

// #Fase0-B1 — estado del autoguardado. Antes solo se mostraba "Guardado
// HH:MM" aunque hubiera cambios sin mandar o el último guardado fallara.
function SaveStatus({ saving, saveFailed, hasPendingChanges, lastSaved }) {
  if (saving) return <p className="text-xs text-slate-400">Guardando...</p>;
  if (saveFailed) {
    return (
      <p className="text-xs text-red-600 flex items-center gap-1">
        <AlertTriangle size={10} /> No se pudo guardar; se reintentará con el siguiente cambio o con "Guardar"
      </p>
    );
  }
  if (hasPendingChanges) return <p className="text-xs text-orange-500">Cambios sin guardar...</p>;
  if (!lastSaved) return null;
  return (
    <p className="text-xs text-slate-400 flex items-center gap-1">
      <CheckCircle size={10} /> Guardado {lastSaved.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
    </p>
  );
}

// Section header component - responsive
function SectionHeader({ section, stats, isActive, totalCriteria }) {
  const config = SECTION_CONFIG[section] || { label: section, color: '#1A56DB', range: '' };


  return (
    <div className={`flex flex-col sm:flex-row sm:items-center justify-between px-3 sm:px-4 py-2 sm:py-3 rounded-sm mb-3 sm:mb-4 gap-2 ${isActive ? 'bg-soem-navy' : 'bg-slate-100'}`}>
      <div className="flex items-center gap-2 sm:gap-3">
        <div className="w-1 sm:w-1.5 h-6 sm:h-8 rounded-full flex-shrink-0" style={{ backgroundColor: config.color }} />
        <div className="min-w-0">
          <h3 className={`font-bold text-xs sm:text-sm ${isActive ? 'text-white' : 'text-soem-navy'}`}>
            {config.label}
          </h3>
          <p className={`text-[10px] sm:text-xs ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>
            Criterios {config.range} — {totalCriteria} criterios
          </p>
        </div>
      </div>
      <div className={`text-right flex sm:block items-center justify-end gap-2 ${isActive ? 'text-white' : 'text-soem-navy'}`}>
        <p className="text-base sm:text-lg font-black">{stats.score}/{stats.max}</p>
        <p className={`text-[10px] sm:text-xs ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>
          {percentageLabel(stats.percentage, 0)}
        </p>
      </div>
    </div>
  );
}

export default function EvaluationChecklist() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [evaluation, setEvaluation] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [zones, setZones] = useState([]);
  const [zoneError, setZoneError] = useState(false);
  const [pendingEvidence, setPendingEvidence] = useState(null);
  const [zonesLoading, setZonesLoading] = useState(false);
  const zonesRequestRef = useRef(null);
  const evidenceBusyRef = useRef(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [activeUser, setActiveUser] = useState(null);
  // Usuario asignado como evaluador en esta evaluación (#WiVhw3i0).
  const [assignedLevantador, setAssignedLevantador] = useState(null);

  const [localItems, setLocalItems] = useState({});
  const [generalFields, setGeneralFields] = useState({});
  const readOnly = evaluation?.status === 'publicada' || evaluation?.status === 'en_revision';

  // #Fase0-B1 — estado del autoguardado en refs. Antes el setTimeout del
  // autoguardado llamaba a un saveProgress "congelado" en el render donde
  // se hizo el cambio, cuyo localItems todavía NO traía ese cambio: el
  // último clic se guardaba recién con el siguiente. Con refs, el guardado
  // siempre lee lo más reciente.
  const localItemsRef = useRef({});
  const generalFieldsRef = useRef({});
  const evaluationRef = useRef(null);
  const naBackupsRef = useRef({});
  const inFlightNARef = useRef(new Set());
  const dateTouchedRef = useRef(new Set());
  const fieldRevisionsRef = useRef({});
  const refreshCounterRef = useRef(0);
  const [remoteNAChanges, setRemoteNAChanges] = useState([]);
  const dirtyRef = useRef({});        // { [key]: { [campo]: revisión } }
  const gfDirtyRevRef = useRef(0);    // 0 = general_fields sin cambios
  const gfUserChangedRef = useRef(false); // el usuario (no el pre-llenado) tocó general_fields
  const revRef = useRef(0);
  const autoSaveTimerRef = useRef(null);
  const saveChainRef = useRef(Promise.resolve(true));
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  // #Fase2-B2 — Reintentar el catálogo no descarta los archivos ni duplica peticiones en vuelo.
  const loadZones = useCallback(() => {
    if (zonesRequestRef.current) return zonesRequestRef.current;
    setZonesLoading(true);
    setZoneError(false);
    const request = zonesAPI.list().then(({ data }) => {
      setZones(data);
    }).catch(() => {
      setZoneError(true);
    }).finally(() => {
      zonesRequestRef.current = null;
      setZonesLoading(false);
    });
    zonesRequestRef.current = request;
    return request;
  }, []);

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  useEffect(() => { evaluationRef.current = evaluation; }, [evaluation]);

  // Build template-driven sections (#198). Each section keeps the criteria
  // list enriched with question_type/options so the legacy CRITERIA
  // catalog isn't needed when the eval has a template_id.
  const isTemplateMode = !!evaluation?.template_id && (evaluation?.sections || []).length > 0;
  const templateSections = isTemplateMode
    ? evaluation.sections.map((s, index) => ({
        name: sectionKey(s, index),
        label: sectionTitle(s),
        criteria: (s.criteria || []).map(c => ({
          ...c,
          event_id: evaluation.event_id || null,
          id: c.criterion_id ?? c.question_id,
          // #Fase0-B2 — en plantillas migradas id es el entero legacy, pero
          // blocked_items de la API trae el question_id (UUID); se guarda
          // para poder resolver el título del bloqueante.
          question_id: c.question_id ?? null,
          section: s.section_name || s.section_id,
          title: c.title || c.criterion_text || `Pregunta`,
          focus: c.focus || c.description || '',
          // #RecintoSeguro — texto del estándar de recinto seguro
          recintoSeguro: c.recinto_seguro || '',
          blocker: c.is_blocking || false,
          question_type: c.question_type || 'score_scale',
          options: c.options || [],
          max_score: c.max_score ?? 10,
          // #Fase1-10 — Cada tipo conserva el default de calificación definido por el contrato.
          scoring_enabled: c.scoring_enabled,
          // #Fase0-B2 / C5 — la API ya expone la configuración del reactivo.
          // is_required: true si falta (evaluaciones legacy o API vieja).
          is_required: c.is_required !== false,
          min_value: c.min_value ?? null,
          max_value: c.max_value ?? null,
          placeholder: c.placeholder ?? null,
        })),
      }))
    : Object.values(SECTIONS).map(name => ({
        name,
        criteria: getCriteriaBySection(name),
      }));
  const sections = templateSections.map(s => s.name);
  const sectionLabels = Object.fromEntries(
    templateSections.map((s) => [s.name, s.label || s.name])
  );
  const criteriaByName = Object.fromEntries(templateSections.map(s => [s.name, s.criteria]));

  const [activeSection, setActiveSection] = useState(sections[0] || SECTIONS.EVENTOS);
  // Keep activeSection in range as the evaluation/template loads.
  useEffect(() => {
    if (sections.length > 0 && !sections.includes(activeSection)) {
      setActiveSection(sections[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections.join('|')]);

  // Index of template-provided metadata keyed by criterion_id (which the
  // backend folds question_id into for template evals). Lets the existing
  // section-by-section render pick up question_type/options/title/focus
  // from the template instead of the hardcoded CRITERIA catalog (#198).
  const templateMetaByCid = indexTemplateCriteria(evaluation?.sections || []);

  // Get valid criterio IDs from evaluation items
  const validCriterioIds = new Set(Object.keys(localItems).filter(k => k !== 'null' && k !== 'undefined'));
  if (validCriterioIds.size === 0 && evaluation?.items) {
    evaluation.items.forEach(item => {
      const key = item.criterio_id ?? item.question_id;
      if (key !== undefined && key !== null) validCriterioIds.add(String(key));
    });
  }
  // Fallback: use all template criteria (or hardcoded CRITERIA) if no items yet
  if (validCriterioIds.size === 0) {
    if (isTemplateMode) {
      templateSections.forEach(s => s.criteria.forEach(c => validCriterioIds.add(String(c.id))));
    } else {
      CRITERIA.forEach(c => validCriterioIds.add(String(c.id)));
    }
  }

  // #Fase1-2 — Visibilidad con respuestas locales; puntajes solo del servidor.
  const allCriteria = templateSections.flatMap(s => s.criteria);
  const visible = visibilityMap(allCriteria, localItems);
  const sectionStats = getSectionStatsFromEval(evaluation);

  // #198: completion no longer assumes score_scale. Use the type-aware helper
  // so dropdown, yes_no, text, etc. also count as answered.
  // #Fase0-B2 — el avance se cuenta sobre los reactivos que se muestran (no
  // sobre items huérfanos de preguntas que ya no están en la plantilla, que
  // nadie puede contestar), y el envío solo espera a los obligatorios
  // (is_required !== false, contrato C5), igual que /submit en la API.
  const shownCriteria = allCriteria.filter(
    (c) => visible[c.id] !== false && c.question_type !== 'info'
  );
  const progress = evaluationProgress(shownCriteria, localItems, visible);
  const completedItems = progress.completed;
  const totalItems = progress.total;
  const progressPct = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
  const pendingItems = localMissing(allCriteria, localItems, visible);
  const pendingRequired = pendingItems.length;

  // #Fase0-B2 — el puntaje global es el que calcula la API (ponderado por
  // sección) y llega en la respuesta del PUT /items o del GET. Antes aquí se
  // hacía score/(totalItems*10), que contaba texto, fechas, etc. como si
  // fueran de 10 puntos y no coincidía con lo que después veían el líder y
  // el cliente. Se actualiza cada vez que termina un guardado.
  const currentScore = evaluation?.score_summary ? evaluation.score : '—';
  const maxPossibleScore = evaluation?.score_summary ? evaluation.max_score : '—';
  const currentPct = evaluation?.score_summary?.percentage ?? null;
  const currentSemaforo = evaluation?.score_summary?.semaforo || 'sin_calificacion';

  useEffect(() => {
    const load = async () => {
      try {
        const [evalRes, usersRes] = await Promise.all([
          evaluationsAPI.get(id),
          usersAPI.list(),
        ]);
        setEvaluation(evalRes.data);
        // Index items by criterio_id when present, otherwise by question_id
        // so template evaluations (no legacy_criterio_id) don't collapse
        // every item under the `null` key (#198).
        const items = serverItems(evalRes.data);
        // #Fase1-10 — Una fecha recibida ya fue contestada; N/A no debe volver a autollenarla.
        dateTouchedRef.current = new Set(
          Object.entries(items)
            .filter(([, item]) => item.answer_date)
            .map(([key]) => key)
        );
        localItemsRef.current = items;
        dirtyRef.current = {};
        setLocalItems(items);
        // #AutoFechaHoy — si la eval aún no tiene fecha, pre-llenarla
        // con la fecha de HOY (formato yyyy-mm-dd para input type=date).
        // El usuario puede modificarla. Antes este campo quedaba vacío
        // y el levantador tenía que escribirla a mano cada vez.
        const gf = { ...(evalRes.data.general_fields || {}) };
        gfDirtyRevRef.current = 0;
        gfUserChangedRef.current = false;
        if (!gf.fecha) {
          const today = new Date();
          const pad = (n) => String(n).padStart(2, '0');
          gf.fecha = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
          // #Fase0-B1 — la fecha pre-llenada cuenta como cambio pendiente
          // para que el primer guardado la persista (antes se mandaba
          // general_fields completo en cada autoguardado).
          gfDirtyRevRef.current = ++revRef.current;
        }
        generalFieldsRef.current = gf;
        setGeneralFields(gf);
        if (evalRes.data.project_id) {
          const projRes = await projectsAPI.get(evalRes.data.project_id);
          setProject(projRes.data);
        }
        const allUsersList = usersRes.data.items || [];
        const currentUser = allUsersList.find(u => u.id === user?.id);
        setActiveUser(currentUser);
        // Resolver el LEVANTADOR ASIGNADO (no quien está viendo)
        // Antes la pantalla mostraba siempre el nombre del usuario
        // activo aunque el levantador asignado fuera otro (#WiVhw3i0).
        const assignedId = evalRes.data?.assigned_levantador_id;
        if (assignedId) {
          const lev = allUsersList.find(u => u.id === assignedId);
          if (lev) setAssignedLevantador(lev);
        }
      } catch { toast.error('Error al cargar evaluación'); }
      finally { setLoading(false); }
    };
    load();
  }, [id, user?.id]);

  // ─── Autoguardado (#Fase0-B1) ─────────────────────────────────────────────
  // Un solo guardado a la vez (se encadenan): dos PUT en paralelo podían
  // llegar en desorden y dejar en el servidor un valor viejo.
  const refreshItems = useCallback(async () => {
    const request = ++refreshCounterRef.current;
    const startingRevisions = snapshotDirty(fieldRevisionsRef.current);
    const response = await evaluationsAPI.get(id);
    if (request !== refreshCounterRef.current) return response.data;
    const edited = {};
    Object.entries(fieldRevisionsRef.current).forEach(([key, fields]) => {
      Object.entries(fields).forEach(([field, revision]) => {
        if (revision !== startingRevisions[key]?.[field]) {
          edited[key] = { ...edited[key], [field]: revision };
        }
      });
    });
    const incoming = serverItems(response.data);
    Object.entries(incoming).forEach(([key, item]) => {
      if (item.answer_date) dateTouchedRef.current.add(key);
    });
    const reconciled = reconcileItems(localItemsRef.current, incoming, dirtyRef.current, edited);
    localItemsRef.current = reconciled.items;
    setLocalItems(reconciled.items);
    setRemoteNAChanges((previous) => [...new Set([...previous, ...reconciled.changedNA])]);
    evaluationRef.current = response.data;
    setEvaluation(response.data);
    return response.data;
  }, [id]);

  useEffect(() => {
    const onFocus = () => {
      if (evaluationRef.current)
        refreshItems().catch(() => toast.error('No se pudieron actualizar las respuestas.'));
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshItems]);

  const runSave = useCallback(async (silent) => {
    const ev = evaluationRef.current;
    if (!ev || ev.status === 'publicada' || ev.status === 'en_revision') return true;
    // Fecha de Evaluación obligatoria al guardar manualmente (#qhuQuWUq).
    // No se aplica a auto-save (silent=true) para no interrumpir mientras
    // el levantador llena el resto del formulario.
    if (!silent && !generalFieldsRef.current.fecha) {
      toast.error('La Fecha de Evaluación es obligatoria');
      return false;
    }

    // Foto de lo pendiente: se manda SOLO lo que el usuario tocó desde el
    // último guardado exitoso, y de cada item solo esos campos.
    const snapshot = snapshotDirty(dirtyRef.current);
    const items = buildItemsPayload(snapshot, localItemsRef.current);
    const gfRev = gfDirtyRevRef.current;
    const gf = gfRev ? { ...generalFieldsRef.current } : null;
    if (items.length === 0 && !gf) {
      if (!silent) toast.success('Progreso guardado');
      return true;
    }

    setSaving(true);
      items
        .filter((item) => item.not_applicable)
        .forEach((item) => {
          inFlightNARef.current.add(String(item.criterio_id ?? item.question_id));
        });
      try {
      // general_fields va por su propio endpoint (PUT /items lo ignora) y
      // reemplaza el objeto completo, por eso solo se manda si cambió.
      const [itemsRes, gfRes] = await Promise.allSettled([
        items.length > 0 ? evaluationsAPI.updateItems(id, items) : Promise.resolve(null),
        gf ? evaluationsAPI.updateGeneralFields(id, gf) : Promise.resolve(null),
      ]);

      if (itemsRes.status === 'fulfilled' && items.length > 0) {
        // Lo editado mientras el PUT iba en vuelo conserva otra revisión y
        // se queda pendiente para el siguiente guardado.
        clearSaved(dirtyRef.current, snapshot);
          Object.keys(snapshot).forEach((key) => {
            if (snapshot[key].not_applicable) delete naBackupsRef.current[key];
          });
          const data = itemsRes.value?.data || {};
          // #Fase1-2 — Una pregunta eliminada sale de la cola incluso si cambió en vuelo.
          clearSkipped(dirtyRef.current, data.skipped, localItemsRef.current);
          // #Fase1-10 — El GET concilia decisiones de otros roles sin pisar ediciones locales.
          try {
            await refreshItems();
          } catch {
            toast.error(
              'Cambios guardados; no se pudieron actualizar las respuestas del servidor.'
            );
          }
        }
      if (gfRes.status === 'fulfilled' && gf) {
        if (gfDirtyRevRef.current === gfRev) {
          gfDirtyRevRef.current = 0;
          gfUserChangedRef.current = false;
        }
        setEvaluation((prev) => ({ ...prev, general_fields: gf }));
      }

      const failed = [itemsRes, gfRes].find((r) => r.status === 'rejected');
      setHasPendingChanges(hasDirtyItems(dirtyRef.current) || gfDirtyRevRef.current > 0);
      if (failed) {
        setSaveFailed(true);

          if (!silent) toast.error(errorMessage(failed.reason, 'Error al guardar'));
        return false;
      }
      setSaveFailed(false);
      setLastSaved(new Date());
      if (!silent) toast.success('Progreso guardado');
      return true;
      } finally {
        inFlightNARef.current.clear();
        setSaving(false); }
    }, [id, refreshItems]);

  const saveProgress = useCallback((silent = false) => {
    const next = saveChainRef.current.then(() => runSave(silent)).catch(() => false);
    saveChainRef.current = next;
    return next;
  }, [runSave]);

  const scheduleAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      saveProgress(true);
    }, 2500);
  }, [saveProgress]);

  // Al salir de la pantalla con cambios pendientes, se mandan de una vez en
  // lugar de esperar un temporizador que ya no tendría pantalla. Solo si el
  // usuario cambió algo: abrir la evaluación para verla no debe fijar la
  // fecha pre-llenada.
  useEffect(() => () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    if (hasDirtyItems(dirtyRef.current) || gfUserChangedRef.current) saveProgress(true);
  }, [saveProgress]);

  // Aplica un cambio del usuario a un item y lo marca pendiente de guardar.
  const applyItemChange = (criterioId, partial) => {
    // #Fase1-2 — N/A limpia respuestas locales sin enviarlas junto a la marca.
    const responseFields = RESPONSE_FIELDS;
    if ('answer_date' in partial) dateTouchedRef.current.add(String(criterioId));
    if (responseFields.some((field) => field in partial))
      partial = { ...partial, _answerEdited: true };
    if (
      responseFields.some((k) => k in partial) &&
      isTemplateMode &&
      localItemsRef.current[criterioId]?.not_applicable
    )
      partial = { ...partial, not_applicable: false, na_reason: '' };
    if (partial.not_applicable === true) {
      const current = localItemsRef.current[criterioId] || {};
      naBackupsRef.current[criterioId] = captureBeforeNA(current, dirtyRef.current[criterioId]);
      localItemsRef.current = {
        ...localItemsRef.current,
        [criterioId]: {
          ...current,
          score: 0,
          score_set: false,
          has_been_scored: false,
          answer_text: null,
          answer_numeric: null,
          answer_date: null,
          answer_bool: null,
          selected_options: [],
        },
      };
      responseFields.forEach((k) => {
        if (dirtyRef.current[criterioId]) delete dirtyRef.current[criterioId][k];
      });
    }
    const prevItem = localItemsRef.current[criterioId] || {};
    const next = {
      ...localItemsRef.current,
      [criterioId]: { ...prevItem, criterio_id: prevItem.criterio_id ?? criterioId, ...partial },
    };
    localItemsRef.current = next;
    setLocalItems(next);
    if (markDirty(dirtyRef.current, criterioId, partial, ++revRef.current)) {
      setHasPendingChanges(true);
      markDirty(fieldRevisionsRef.current, criterioId, partial, revRef.current);
      scheduleAutoSave();
    }
  };

  const handleScoreChange = (criterioId, score) => {
    applyItemChange(criterioId, { score, has_been_scored: true, score_set: true });
  };

  // Generic handler for non-score-scale answer types (#198).
  const handleAnswerChange = async (criterioId, partial) => {
    // #Fase1-10 — Un clic accidental se puede deshacer antes de enviar la marca.
    const backup = naBackupsRef.current[criterioId];
    if (partial.not_applicable === false && backup) {
      if (!inFlightNARef.current.has(String(criterioId))) {
        const restored = undoPendingNA(
          localItemsRef.current[criterioId],
          dirtyRef.current[criterioId],
          backup
        );
        localItemsRef.current = { ...localItemsRef.current, [criterioId]: restored.item };
        dirtyRef.current[criterioId] = restored.dirty;
        if (!Object.keys(restored.dirty).length) delete dirtyRef.current[criterioId];
        delete naBackupsRef.current[criterioId];
        markDirty(fieldRevisionsRef.current, criterioId, restored.item, ++revRef.current);
        setLocalItems(localItemsRef.current);
        setHasPendingChanges(hasDirtyItems(dirtyRef.current));
        return;
      }
      if (!(await saveProgress(true))) return;
    }
    applyItemChange(criterioId, partial);
  };

  // #Fase0-B1 — antes las notas solo se guardaban si después se calificaba
  // algo; ahora cuentan como cambio y disparan el autoguardado. Vaciarlas
  // manda "" para que también se borren en el servidor.
  const handleNotesChange = (criterioId, notes) => {
    applyItemChange(criterioId, { notes });
  };

  const handleGeneralFieldChange = (field, value) => {
    const next = { ...generalFieldsRef.current, [field]: value };
    generalFieldsRef.current = next;
    setGeneralFields(next);
    gfDirtyRevRef.current = ++revRef.current;
    gfUserChangedRef.current = true;
    setHasPendingChanges(true);
    scheduleAutoSave();
  };

  const handleLocationUpdate = async (criterioId) => {
    const location = await captureLocation();
    if (location) applyItemChange(criterioId, { location });
    else toast.error('No se pudo obtener GPS. Selecciona la zona del reactivo.');
  };

  const handleEvidenceUpload = async (criterioId, files, capture = null) => {
    // #Fase2-B2 — El ref cierra también la carrera entre dos clics antes del siguiente render.
    if (evidenceBusyRef.current && !capture) return;
    evidenceBusyRef.current = true;
    let awaitingZone = false;
    setUploading(criterioId);
    try {
      // #Fase2-B2 — Conserva archivos y hora mientras el usuario elige la zona sin GPS.
      const captured_at = capture?.captured_at || new Date().toISOString();
      const location = capture ? capture.location : await captureLocation();
      const zone = capture?.zone || localItemsRef.current[criterioId]?.zone;
      if (!location && !zone?.id) {
        awaitingZone = true;
        setPendingEvidence({ criterioId, files, location, captured_at, zone: null });
        if (!zones.length) loadZones();
        return;
      }
      for (const file of files) {
        if (file.type === 'application/pdf' && file.size > 25 * 1024 * 1024) {
          const error = new Error('El PDF supera el límite de 25 MB');
          error.local = true;
          throw error;
        }
        const metadata = {
          captured_at, geo_source: location?.source || 'none',
          lat: location?.lat, lng: location?.lng, accuracy_m: location?.accuracy_m,
          zone_id: zone?.id, zone_detail: zone?.detail,
        };
        const r = await evaluationsAPI.uploadEvidence(id, criterioId, file, metadata);
        if (location && !localItemsRef.current[criterioId]?.location) {
          applyItemChange(criterioId, { location });
        }
        const url = r?.data?.file_url || r?.data?.url;
        if (!url) throw new Error('El servidor no devolvió la URL del archivo');
        // La evidencia la guarda el endpoint de subida; no se marca como
        // pendiente del autoguardado (PUT /items no la recibe).
        const prevItem = localItemsRef.current[criterioId] || {};
        localItemsRef.current = {
          ...localItemsRef.current,
          [criterioId]: {
            ...prevItem, evidence_urls: [...(prevItem.evidence_urls || []), url],
            evidence: [...(prevItem.evidence || []), { ...r.data, file_url: url }],
          },
        };
        setLocalItems(localItemsRef.current);
      }
      toast.success(`${files.length} archivo(s) subido(s)`);
      // Refresh evaluation to get server-side URLs
      try {
        const refreshed = await evaluationsAPI.get(id);
        setEvaluation(refreshed.data);
        if (refreshed.data?.items) {
          // #Fase0-B1 — mismo índice que al cargar (criterio_id o
          // question_id). Antes solo criterio_id: en plantillas nuevas es
          // null y todo caía en la llave "null", que luego el autoguardado
          // mandaba como un item sin identidad.
          const merged = { ...localItemsRef.current };
          for (const it of refreshed.data.items) {
            const k = it.criterio_id ?? it.question_id;
            if (k === undefined || k === null || !it.evidence_urls?.length) continue;
            merged[k] = { ...merged[k], evidence_urls: it.evidence_urls, evidence: it.evidence };
          }
          localItemsRef.current = merged;
          setLocalItems(merged);
        }
      } catch {}
    } catch (error) {
      // #Fase1-10 — El 502 de almacenamiento es transitorio; conservar el detalle invita a reintentar.
      // #Fase2-B4 — Los errores de red/proxy no deben reemplazar el mensaje legible con texto de Axios.
      const fallback = error.local ? error.message : 'No se pudo guardar el archivo, intenta de nuevo';
      toast.error(errorMessage(error, fallback));
    }
    finally {
      if (!awaitingZone) {
        evidenceBusyRef.current = false;
        setUploading(null);
      }
    }
  };

  const handleEvidenceDelete = async (criterioId, url) => {
    if (!window.confirm('¿Borrar esta evidencia de la evaluación?')) return;
    try {
      await evaluationsAPI.deleteEvidence(id, criterioId, url);
      const item = localItemsRef.current[criterioId] || {};
      applyItemChange(criterioId, {
        evidence_urls: (item.evidence_urls || []).filter((value) => value !== url),
        evidence: (item.evidence || []).filter((entry) => entry.file_url !== url),
      });
    } catch (error) {
      toast.error(errorMessage(error, 'Error al borrar evidencia'));
      return;
    }
    try {
      await refreshItems();
    } catch {
      toast.error('Evidencia borrada; no se pudo actualizar el resumen.');
    }
  };

  const handleSubmit = async () => {
    setMissing([]);
    setSubmitting(true);
    try {
      // #Fase0-B1 — primero se guarda lo pendiente; si ese guardado falla
      // no se envía, para no mandar a revisión algo distinto a lo que se ve.
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      const saved = await saveProgress(true);
      if (!saved) {
        toast.error('No se pudieron guardar los últimos cambios. Revisa tu conexión e intenta de nuevo.');
        return;
      }
      const latest = await refreshItems();
      if (['lider_proyecto', 'super_admin'].includes(user?.role)) {
        if (latest.is_blocked && !window.confirm(publicationWarning(latest) + '¿Continuar?'))
          return;
      }
      const submitResult = await evaluationsAPI.submit(id);
      const ev = submitResult.data;

      try {
        const projectData = await projectsAPI.get(ev.project_id || evaluation.project_id);
        const usersData = await usersAPI.list();
        const leader = (usersData.data.items || []).find(u => u.id === projectData.data.leader_id);
        if (leader) {
          const evalName = ev.title || evaluation.title || generalFields.instalacion || `Evaluación v${ev.version || evaluation.version}`;
          await notifySubmitted({
            leaderEmail: leader.email,
            leaderName: leader.name,
            senderName: activeUser?.name || 'Levantador',
            evaluationName: evalName,
            version: ev.version || evaluation.version,
            projectName: projectData.data.name,
            semaforo: ev.semaforo || 'critico',
            score: ev.score || 0,
            maxScore: ev.max_score || 350,
            percentage: evaluationPercentage(ev),
          });
          toast.success('Evaluación enviada a revisión — Líder notificado por email');
        } else {
          toast.success('Evaluación enviada a revisión');
        }
      } catch {
        toast.success('Evaluación enviada a revisión');
      }

      navigate('/dashboard/proyectos');
    } catch (err) {
      setMissing(missingFromError(err));
      toast.error(errorMessage(err, 'Error al enviar'));
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin w-8 h-8 border-4 border-soem-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="soem-card p-12 text-center">
        <p className="text-slate-400">Evaluación no encontrada</p>
      </div>
    );
  }

  // Get criteria for current section
  const currentSectionCriteria = (criteriaByName[activeSection] || []).filter(
    (c) =>
      (c.question_type === 'info' || validCriterioIds.has(String(c.id))) && visible[c.id] !== false
  );
  const currentStats = sectionStats[activeSection] || { score: 0, max: 0, percentage: null };

  return (
    <div className="space-y-4 lg:space-y-5">
      {/* Header - responsive */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="soem-btn-secondary p-2">
            <ArrowLeft size={16} />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-base lg:text-lg font-black text-soem-navy truncate">
              {evaluation.title || generalFields.instalacion || `Evaluación v${evaluation.version}`}
            </h1>
            <p className="text-slate-500 text-xs truncate">
              {project?.name} · <StatusBadge status={displayStatus(evaluation)} />
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!readOnly && (
            <>
              <button onClick={() => saveProgress(false)} disabled={saving}
                className="soem-btn-secondary px-3 py-2 text-xs sm:text-sm flex items-center gap-1.5 disabled:opacity-50">
                <Save size={14} /> <span className="hidden sm:inline">{saving ? 'Guardando...' : 'Guardar'}</span><span className="sm:hidden">{saving ? '...' : ''}</span>
              </button>
              <button onClick={handleSubmit} disabled={submitting || pendingRequired > 0}
                className="soem-btn-primary px-3 py-2 text-xs sm:text-sm flex items-center gap-1.5 disabled:opacity-50"
                title={pendingRequired > 0
                  ? `Faltan ${pendingRequired} reactivo(s) pendientes por contestar`
                  : 'Enviar a revisión'}>
                <Send size={14} />
                <span className="hidden sm:inline">{submitting ? 'Enviando...' : 'Enviar'}</span>
              </button>
            </>
          )}
          <button onClick={() => navigate(`/dashboard/evaluaciones/${id}/mapa`)}
            className="soem-btn-secondary px-3 py-2 text-xs sm:text-sm flex items-center gap-1.5" title="Mapa de la locación">
            <Map size={14} /> <span className="hidden sm:inline">Mapa</span>
          </button>
          <ReportDownload evaluation={evaluation} />
        </div>
      </div>

      <MissingList missing={missing.length ? missing : pendingItems} />
      {remoteNAChanges.length > 0 && (
        <p role="status" className="bg-blue-50 p-3 text-sm">
          Se actualizó No aplica desde el servidor en:{' '}
          {allCriteria
            .filter((c) => remoteNAChanges.includes(String(c.id)))
            .map((c) => c.code || c.title)
            .join(', ')}
          .
        </p>
      )}
      <ScoreSummary evaluation={evaluation} />
      {/* Main layout - responsive */}
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-5">
        {/* Sidebar - hidden on mobile by default, shown as bottom sheet or collapsible */}
        <div className="hidden lg:block w-72 flex-shrink-0 space-y-4 sticky top-4 self-start">
          {/* Progress card */}
          <div className="soem-card p-4">
            <p className="soem-label mb-2">Progreso</p>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500">Criterios completados</span>
              <span className="text-sm font-bold text-soem-primary">{completedItems}/{totalItems}</span>
            </div>
            <div className="bg-slate-100 rounded-full h-2.5 mb-3">
              <div className="bg-soem-primary h-2.5 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
            </div>
            {pendingRequired > 0 && (
              <p className="text-xs text-orange-600 mb-1">Pendientes: {pendingRequired}</p>
            )}
            <SaveStatus saving={saving} saveFailed={saveFailed} hasPendingChanges={hasPendingChanges} lastSaved={lastSaved} />
          </div>

          {/* Real-time score */}
          <div className="soem-card p-4">
            <p className="soem-label mb-3">Puntaje en Tiempo Real</p>
            {hasPendingChanges && (
              <p className="text-[10px] text-slate-400 text-center -mt-2 mb-2">Se actualiza al guardar los cambios</p>
            )}
            <div className="text-center mb-3">
              <SemaforoBadge semaforo={currentSemaforo} percentage={currentPct} size="md" />
            </div>
            <p className="text-3xl font-black text-soem-navy text-center">{currentScore}/{maxPossibleScore}</p>
            <p className="text-slate-400 text-xs text-center mb-3">{percentageLabel(currentPct)}</p>
            
            {currentPct != null && <DictamenCard percentage={currentPct} compact />}

            {/* Radar chart — shown on read-only states so the published view
                keeps showing the section breakdown chart (was lost after a
                refactor that moved the chart to EvaluationReview only). */}
            {readOnly && (
              <div className="mt-3">
                <SectionRadarChart sectionStats={sectionStats} size={220} />
              </div>
            )}

            {/* Section breakdown */}
            <div className="mt-4 space-y-2">
              {Object.entries(sectionStats).map(([sec, s]) => {
                const pct = s.percentage ?? 0;
                // #Fase0-B4 — corte único del semáforo (C1), antes ≥81/≥61/≥41.
                const color = s.percentage == null ? '#94a3b8' : getSemaforoBarColor(pct);
                return (
                  <div key={sec}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-bold text-slate-600">
                        {s.title || sectionLabels[sec] || sec}
                      </span>
                      <span className="font-mono" style={{ color }}>{s.score}/{s.max}</span>
                    </div>
                    <div className="bg-slate-100 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Blockers */}
          {evaluation.is_blocked && (
            <div className="soem-card p-4 border-2 border-red-300 bg-red-50" data-testid="blockers-panel">
              <p className="soem-label text-red-700 mb-2 flex items-center gap-1">
                <AlertTriangle size={12} /> Criterios Bloqueantes
              </p>
              <p className="text-xs text-red-600 mb-2">{evaluation.blocked_items?.length || 0} criterio(s) con puntaje 0</p>
              <div className="space-y-1">
                {(evaluation.blocked_items || BLOCKER_CRITERIA).slice(0, 6).map(bid => {
                  // #BlockerTitleLookup — antes mostraba el UUID crudo
                  // (#8c354278-...) porque CRITERIA es el catálogo
                  // legacy de 35 criterios y no contiene los question_id
                  // de plantillas. Ahora buscamos primero en
                  // templateSections (flat), luego CRITERIA como fallback
                  // para evals legacy. Si nada, mostramos solo "Criterio".
                  // #Fase0-B2 — blocked_items trae el question_id (UUID) en
                  // evaluaciones de plantilla y el criterio_id entero en
                  // legacy/huérfanas. En plantillas migradas (preguntas con
                  // legacy_criterio_id, como la plantilla por defecto) los
                  // criterios y localItems se indexan por el entero, así que
                  // se busca por las dos llaves.
                  const sameId = (a, b) => a !== undefined && a !== null && String(a) === String(b);
                  let title = null;
                  if (isTemplateMode) {
                    for (const sec of templateSections) {
                      const found = (sec.criteria || []).find(c => sameId(c.id, bid) || sameId(c.question_id, bid));
                      if (found) { title = found.title; break; }
                    }
                  }
                  if (!title) {
                    const crit = CRITERIA.find(c => c.id === bid);
                    title = crit?.title;
                  }
                  const item = localItems[bid]
                    || Object.values(localItems).find(it => sameId(it?.question_id, bid));
                  // #Fase1-10 — La respuesta local manda; sin respuesta disponible se conserva el bloqueo de API.
                  const question =
                    allCriteria.find(
                      (criterion) => sameId(criterion.id, bid) || sameId(criterion.question_id, bid)
                    ) || {};
                  const localVisible = visible[question.id] !== false;
                  const hasAnswer = isScored(item) || typeof item?.answer_bool === 'boolean';
                  const isFailed = item?.not_applicable || !localVisible
                      ? false
                      : hasAnswer
                        ? isCriticalFinding({ ...question, visible: localVisible }, item, true)
                        : Array.isArray(evaluation.blocked_items);
                  return (
                    <div key={bid} data-testid={`blocker-${bid}`} data-failed={isFailed ? 'true' : 'false'} className={`text-xs px-2 py-1 rounded flex items-start gap-1.5 ${isFailed ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      <span className="flex-1 leading-tight">{title || 'Criterio sin título'}</span>
                      {isFailed ? <AlertTriangle size={10} className="flex-shrink-0 mt-0.5" /> : <CheckCircle size={10} className="flex-shrink-0 mt-0.5" />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-4 lg:space-y-6">
          {/* Mobile progress summary - enhanced for mobile */}
          <div className="lg:hidden soem-card p-3 sm:p-4">
            <div className="flex items-center justify-between gap-3 sm:gap-4 mb-3">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] sm:text-xs text-slate-500">Progreso</span>
                  <span className="text-xs sm:text-sm font-bold text-soem-primary">{completedItems}/{totalItems}</span>
                </div>
                <div className="bg-slate-100 rounded-full h-1.5 sm:h-2">
                  <div className="bg-soem-primary h-1.5 sm:h-2 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
              <div className="text-center flex-shrink-0">
                <SemaforoBadge semaforo={currentSemaforo} percentage={currentPct} size="sm" />
                <p className="text-sm sm:text-lg font-black text-soem-navy mt-0.5 sm:mt-1">{currentScore}/{maxPossibleScore}</p>
              </div>
            </div>
            {/* Mini section breakdown for mobile */}
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-3 px-3 scrollbar-hide">
              {Object.entries(sectionStats).map(([sec, s]) => {
                const pct = s.percentage ?? 0;
                // #Fase0-B4 — corte único del semáforo (C1), antes ≥81/≥61/≥41.
                const color = s.percentage == null ? '#94a3b8' : getSemaforoBarColor(pct);
                const isCurrentSection = sec === activeSection;
                return (
                  <button
                    key={sec}
                    onClick={() => setActiveSection(sec)}
                    className={`flex-shrink-0 px-2 py-1.5 rounded text-[10px] sm:text-xs border transition-colors ${
                      isCurrentSection ? 'bg-slate-100 border-slate-300' : 'bg-white border-slate-200'
                    }`}
                  >
                    <span className="font-bold block" style={{ color }}>
                      {s.title || sectionLabels[sec] || sec}
                    </span>
                    <span className="text-slate-500">{s.score}/{s.max}</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-2 text-[10px]">
              <SaveStatus saving={saving} saveFailed={saveFailed} hasPendingChanges={hasPendingChanges} lastSaved={lastSaved} />
            </div>
          </div>

          {/* General fields - read-only for location info, editable for date */}
          {!readOnly && (
            <div className="soem-card p-4 lg:p-5">
              <h3 className="font-bold text-soem-navy mb-3 lg:mb-4 text-sm lg:text-base">Información General de la Locación</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
                {/* #NoLocationPrompt — antes esta sección tenía un select
                    para escoger la locación. Es redundante: la evaluación
                    pertenece a un evento que ya tiene location_id y address.
                    Ahora mostramos la locación heredada del evento como
                    read-only. Si no hay locación heredada, fallback al
                    project/general_fields. El selector se quitó. */}
                <div>
                  <label htmlFor="f-nombre-de-la-locación" className="soem-label block mb-1">Nombre de la Locación</label>
                  <div
                    id="f-nombre-de-la-locación"
                    className="w-full px-3 py-2 bg-soem-bg-alt border border-slate-200 rounded-sm text-sm text-gray-700 cursor-default"
                    data-testid="general-instalacion"
                  >
                    {evaluation?.location_info?.name || project?.location?.name || generalFields.instalacion || <span className="text-slate-400">Heredada del evento</span>}
                  </div>
                </div>
                <div>
                  <label htmlFor="f-ubicación-dirección" className="soem-label block mb-1">Ubicación / Dirección</label>
                  <div
                    className="w-full px-3 py-2 bg-soem-bg-alt border border-slate-200 rounded-sm text-sm text-gray-700 cursor-default"
                    data-testid="general-ubicacion"
                  >
                    {generalFields.ubicacion
                    ||
                      formatAddress(evaluation?.location_info?.address) ||
                      formatAddress(project?.location?.address)
                      || (
                        <span className="text-slate-400">Heredada del evento</span>
                      )}
                  </div>
                </div>
                <div>
                  <label htmlFor="f-nombre-del-evaluador" className="soem-label block mb-1">Nombre del Evaluador</label>
                  <div
                    className="w-full px-3 py-2 bg-soem-bg-alt border border-slate-200 rounded-sm text-sm text-gray-700 cursor-default"
                    data-testid="general-evaluador"
                  >
                    {/* #WiVhw3i0 — Prioriza al levantador asignado;
                        después los datos guardados y el usuario activo. */}
                    {assignedLevantador?.name || evaluation?.location_info?.evaluator_name || generalFields.evaluador || activeUser?.name || <span className="text-slate-400">Sin información</span>}
                  </div>
                </div>
                <div>
                  <label htmlFor="f-fecha-de-evaluación" className="soem-label block mb-1">Fecha de Evaluación</label>
                  <input
id="f-fecha-de-evaluación"                     type="date"
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                    value={generalFields.fecha || ''}
                    onChange={(e) => handleGeneralFieldChange('fecha', e.target.value)}
                    data-testid="general-fecha"
                  />
                </div>
                <div>
                  <label htmlFor="f-correo" className="soem-label block mb-1">Correo</label>
                  <div 
                    className="w-full px-3 py-2 bg-soem-bg-alt border border-slate-200 rounded-sm text-sm text-gray-700 cursor-default"
                    data-testid="general-correo"
                  >
                    {assignedLevantador?.email || evaluation?.location_info?.evaluator_email || generalFields.correo || <span className="text-slate-400">Sin información</span>}
                  </div>
                </div>
                <div>
                  <label htmlFor="f-teléfono" className="soem-label block mb-1">Teléfono</label>
                  <div 
                    className="w-full px-3 py-2 bg-soem-bg-alt border border-slate-200 rounded-sm text-sm text-gray-700 cursor-default"
                    data-testid="general-telefono"
                  >
                    {assignedLevantador?.phone || evaluation?.location_info?.evaluator_phone || generalFields.telefono || <span className="text-slate-400">Sin información</span>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section tabs with detailed info */}
          <div className="space-y-4">
            {/* Section selector - scrollable on mobile */}
            <div className="flex gap-1 border-b border-slate-200 overflow-x-auto pb-px -mx-4 px-4 lg:mx-0 lg:px-0 scrollbar-hide">
              {sections.map((s) => {
                const sectionCount = (criteriaByName[s] || []).filter(c => validCriterioIds.has(String(c.id))).length;
                const stats = sectionStats[s] || { score: 0, max: 0, percentage: null };
                return (
                  <button
                    key={s}
                    onClick={() => setActiveSection(s)}
                    className={`px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeSection === s
                        ? 'border-soem-primary text-soem-primary bg-blue-50/50'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                    data-testid={`section-tab-${s}`}
                  >
                    <span className="block">{sectionLabels[s]}</span>
                    <span className="text-[10px] sm:text-xs opacity-70">{stats.score}/{stats.max} · {sectionCount}</span>
                  </button>
                );
              })}
            </div>

            {/* Section header */}
            <SectionHeader
              section={sectionLabels[activeSection]}
              stats={currentStats}
              isActive={true}
              totalCriteria={currentSectionCriteria.length}
            />

            {/* Criteria list */}
            <div className="space-y-4">
              {currentSectionCriteria.map((c, idx) => {
                // Merge template metadata (question_type, options, etc.)
                // onto the hardcoded CRITERIA entry so CriterionCard can
                // render the right input per #198.
                const meta = templateMetaByCid[c.id];
                const enriched = meta ? {
                  ...c,
                      ...meta,
                      id: c.id,
                  title: meta.title || meta.criterion_text || c.title,
                  focus: meta.focus || meta.description || c.focus,
                  // #RecintoSeguro — prioriza el texto de la plantilla
                  recintoSeguro: meta.recinto_seguro || c.recintoSeguro || '',
                  blocker: meta.is_blocking ?? c.blocker,
                  question_type: meta.question_type || 'score_scale',
                  options: meta.options || [],
                  max_score: meta.max_score ?? 10,
                  scoring_enabled: meta.scoring_enabled,
                  // #Fase0-B2 / C5
                  is_required: meta.is_required !== false,
                  min_value: meta.min_value ?? null,
                  max_value: meta.max_value ?? null,
                  placeholder: meta.placeholder ?? null,
                } : c;
                return (
                  <CriterionCard
                    key={c.id}
                    criterion={{
                      ...enriched,
                      event_id: evaluation.event_id || null,
                      visible: visible[c.id] !== false,
                      _autofillDate: shouldAutofillDate(localItems[c.id] || {}, {
                        readOnly,
                        visible: visible[c.id],
                        dirty: dirtyRef.current[c.id]?.answer_date,
                        attempted: dateTouchedRef.current.has(String(c.id)),
                      }),
                    }}
                    item={{
                      ...c,
                      ...localItems[c.id],
                      notes: localItems[c.id]?.notes ?? c.response_notes,
                    }}
                    readOnly={readOnly}
                    onScoreChange={handleScoreChange}
                    onAnswerChange={handleAnswerChange}
                    onNotesChange={handleNotesChange}
                    allowNA={isTemplateMode}
                    onEvidenceDelete={handleEvidenceDelete}
                    onEvidenceUpload={handleEvidenceUpload}
                    eventId={evaluation.event_id}
                    zones={zones}
                    onLocationUpdate={handleLocationUpdate}
                    uploading={uploading}
                    index={idx}
                    total={currentSectionCriteria.length}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Alert for empty evaluation */}
      {evaluation.status !== 'borrador' && (!evaluation.items || evaluation.items.length === 0) && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-sm p-4 text-center">
          <AlertTriangle size={24} className="text-yellow-600 mx-auto mb-2" />
          <p className="text-yellow-800 font-semibold">Esta evaluación no tiene criterios registrados.</p>
          <p className="text-yellow-600 text-sm">Contacta al administrador para resolver este problema.</p>
        </div>
      )}
      {zoneError && !pendingEvidence && (
        <div role="alert">
          No se pudo cargar el catálogo de zonas.
          <button onClick={loadZones} disabled={zonesLoading}>Reintentar zonas</button>
        </div>
      )}
      {pendingEvidence && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div role="dialog" aria-label="Zona de la evidencia" className="bg-white p-5 w-full max-w-md space-y-4">
            <p>No se pudo obtener GPS. Selecciona la zona para adjuntar la evidencia.</p>
            {zoneError && <p role="alert">No se pudo cargar el catálogo de zonas.</p>}
            {zonesLoading && <p role="status">Cargando zonas...</p>}
            {!zones.length && <button onClick={loadZones} disabled={zonesLoading}>Reintentar zonas</button>}
            <ZoneSelector zones={zones} zone={pendingEvidence.zone}
              onChange={(zone) => setPendingEvidence((value) => ({ ...value, zone }))} />
            <button className="soem-btn-primary p-2" disabled={!pendingEvidence.zone?.id}
              onClick={() => {
                const pending = pendingEvidence;
                applyItemChange(pending.criterioId, { zone: pending.zone });
                setPendingEvidence(null);
                handleEvidenceUpload(pending.criterioId, pending.files, pending);
              }}>Adjuntar</button>
            <button className="soem-btn-secondary p-2" onClick={() => {
              setPendingEvidence(null);
              evidenceBusyRef.current = false;
              setUploading(null);
            }}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}
